/**
 * Scroll-driven 3D scene controller.
 *
 * Every participating element registers once and is driven from a *single*
 * rAF loop and a *single* passive scroll listener — not one listener each —
 * so adding depth to twenty sections costs the same as adding it to one.
 *
 * The loop writes two custom properties per element:
 *   --enter  1 → the element is still below the fold, 0 → it has settled
 *   --exit   0 → the element is in place,             1 → it has left the top
 *
 * CSS turns those into rotateX / translateZ / scale, so the transform work
 * stays on the compositor and JS never touches layout.
 */

type Entry = { el: HTMLElement; intensity: number };

const entries = new Set<Entry>();
let frame = 0;
let listening = false;

function clamp01(n: number) {
  return n < 0 ? 0 : n > 1 ? 1 : n;
}

function update() {
  frame = 0;
  const vh = window.innerHeight;

  for (const { el, intensity } of entries) {
    const rect = el.getBoundingClientRect();

    // Skip anything far off screen: no reads turn into writes, no work done.
    if (rect.bottom < -vh || rect.top > vh * 2) continue;

    // Approaching from below: 1 while off screen, 0 once the top has risen
    // past 20% of the viewport.
    const enter = clamp01((rect.top - vh * 0.2) / (vh * 0.8));

    // Receding past the top: 0 until the bottom passes 45% of the viewport.
    const exit = clamp01((vh * 0.45 - rect.bottom) / (vh * 0.75));

    el.style.setProperty('--enter', (enter * intensity).toFixed(4));
    el.style.setProperty('--exit', (exit * intensity).toFixed(4));
  }
}

function onScroll() {
  if (!frame) frame = requestAnimationFrame(update);
}

function startListening() {
  if (listening) return;
  listening = true;
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
}

function stopListening() {
  if (!listening) return;
  listening = false;
  window.removeEventListener('scroll', onScroll);
  window.removeEventListener('resize', onScroll);
  cancelAnimationFrame(frame);
  frame = 0;
}

/** Returns an unregister function. */
export function registerScene(el: HTMLElement, intensity = 1) {
  const entry: Entry = { el, intensity };
  entries.add(entry);
  startListening();
  onScroll();

  return () => {
    entries.delete(entry);
    el.style.removeProperty('--enter');
    el.style.removeProperty('--exit');
    if (entries.size === 0) stopListening();
  };
}
